// Plan data shared by the pricing cards, plan finder, and comparison table.
// Limits must match DueDash src/utils/subscriptionLimits.ts.

import { webBillingEnabled } from "../../services/webBilling";

export type PlanKey = "free" | "lite" | "pro";
export type BillingCycle = "monthly" | "annual";

export interface Plan {
  key: PlanKey;
  title: string;
  tagline: string;
  monthlyPrice: number;
  // The yearly price shown on the site: the web price once people can buy
  // here, otherwise the app price they'd actually pay.
  annualPrice: number;
  // In-app yearly price; must match App Store / Google Play.
  appAnnualPrice: number;
  // Yearly price on the website (RevenueCat Web Billing), about 2 months free.
  webAnnualPrice: number;
  limits: { events: number; items: number };
  // How full the limit meters on the card look, in percent.
  meter: number;
  featuresHeading: string;
  features: string[];
  badge?: string;
}

export const PLANS: Record<PlanKey, Plan> = {
  free: {
    key: "free",
    title: "Free",
    tagline: "For trying your first few markets.",
    monthlyPrice: 0,
    annualPrice: 0,
    appAnnualPrice: 0,
    webAnnualPrice: 0,
    limits: { events: 10, items: 25 },
    meter: 14,
    featuresHeading: "Includes",
    features: [
      "Booth checkout with inventory and sales tracking by event",
      "Square card payments",
      "Cash, manual, and QR payment methods",
      "Finance overview + expense tracking",
      "Community support",
    ],
  },
  lite: {
    key: "lite",
    title: "Lite",
    tagline: "For regulars with a growing catalog.",
    monthlyPrice: 9.99,
    annualPrice: webBillingEnabled ? 99 : 119,
    appAnnualPrice: 119,
    webAnnualPrice: 99,
    limits: { events: 50, items: 250 },
    meter: 46,
    featuresHeading: "Everything in Free, plus",
    features: [
      "5× the events and 10× the inventory",
      "Email support",
    ],
  },
  pro: {
    key: "pro",
    title: "Pro",
    tagline: "For full-time vendors and teams.",
    monthlyPrice: 24.99,
    annualPrice: webBillingEnabled ? 249 : 299,
    appAnnualPrice: 299,
    webAnnualPrice: 249,
    limits: { events: Infinity, items: Infinity },
    meter: 100,
    featuresHeading: "Everything in Lite, plus",
    features: [
      "Unlimited events and inventory",
      "Multiple inventory pools: keep each show’s stock separate",
      "Employees, with pay and commission tracked per event",
      "Advanced finance analytics",
      "Priority support",
    ],
    badge: "Most popular",
  },
};

export const PLAN_ORDER: PlanKey[] = ["free", "lite", "pro"];

export const formatLimit = (limit: number) =>
  Number.isFinite(limit) ? limit.toLocaleString() : "Unlimited";

export const formatPrice = (price: number) =>
  Number.isInteger(price) ? `$${price}` : `$${price.toFixed(2)}`;

export interface PlanNeeds {
  events: number;
  items: number;
  hasStaff: boolean;
  needsPools: boolean;
}

// The cheapest plan whose limits and features cover the visitor's needs.
export const recommendPlan = (needs: PlanNeeds): PlanKey => {
  if (needs.hasStaff || needs.needsPools) return "pro";
  return (
    PLAN_ORDER.find(
      (key) =>
        needs.events <= PLANS[key].limits.events &&
        needs.items <= PLANS[key].limits.items,
    ) ?? "pro"
  );
};
